import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { TokenStorageService } from './token-storage.service';

export type SortOrder = 'asc' | 'desc';
export type SortField =
  'code' | 'amount' | 'initial_amount' | 'recipient_email' | 'created_at' | 'status';

@Injectable({
  providedIn: 'root',
})
export class VoucherService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
  ) {}

  getVouchers(
    fromDate?: string,
    toDate?: string,
    page: number = 1,
    pageSize: number = 10,
    sortBy?: SortField,
    sortOrder?: SortOrder,
    search?: string,
  ): Observable<PaginatedVoucherResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (fromDate) params = params.set('from_date', `${fromDate}T00:00:00Z`);
    if (toDate) params = params.set('to_date', `${toDate}T23:59:59Z`);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortOrder) params = params.set('sort_order', sortOrder);
    if (search) params = params.set('search', search);

    return this.http.get<PaginatedVoucherResponse>(`${this.apiUrl}/vouchers`, { params });
  }

  getVoucherByCode(code: string): Observable<Voucher> {
    return this.http.get<Voucher>(`${this.apiUrl}/vouchers/${code}`);
  }

  getVoucherRedemptions(code: string): Observable<VoucherRedemption[]> {
    return this.http.get<VoucherRedemption[]>(`${this.apiUrl}/vouchers/${code}/redemptions`);
  }

  /**
   * Create internal voucher(s) for raffles/giveaways, without a checkout.
   * No fees apply, so these are excluded from billing and turnover statistics.
   */
  createInternalVoucher(payload: InternalVoucherCreate): Observable<InternalVoucherCreateResponse> {
    return this.http.post<InternalVoucherCreateResponse>(
      `${this.apiUrl}/vouchers/internal`,
      payload,
    );
  }

  redeemVoucher(verificationKey: string): Observable<{ message: string; redeemed_at: string }> {
    return this.http.post<{ message: string; redeemed_at: string }>(
      `${this.apiUrl}/vouchers/redeem`,
      {
        verification_key: verificationKey,
      },
    );
  }

  redeemPartialVoucher(
    verificationKey: string,
    amount: number,
  ): Observable<{ remaining_amount: number; redeemed_at: string }> {
    return this.http.post<{ remaining_amount: number; redeemed_at: string }>(
      `${this.apiUrl}/vouchers/redeem/partial`,
      {
        verification_key: verificationKey,
        redeem_amount: amount,
      },
    );
  }

  // Public verification endpoint (no authentication required)
  verifyVoucherPublic(verificationKey: string): Observable<PublicVoucherVerification> {
    return this.http.get<PublicVoucherVerification>(
      `${this.apiUrl}/public/vouchers/${verificationKey}/verify`,
    );
  }

  // Public redemption endpoint (no authentication required, requires PIN)
  redeemVoucherPublic(
    verificationKey: string,
    redemptionPin: string,
    redeemAmount?: number,
    redeemingCompanyId?: string,
  ): Observable<PublicRedemptionResponse> {
    const body: Record<string, unknown> = {
      verification_key: verificationKey,
      redemption_pin: redemptionPin,
      redeem_amount: redeemAmount,
    };
    if (redeemingCompanyId) {
      body['redeeming_company_id'] = redeemingCompanyId;
    }
    return this.http.post<PublicRedemptionResponse>(`${this.apiUrl}/public/vouchers/redeem`, body);
  }

  downloadVoucherPDF(code: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/vouchers/${code}/pdf`, {
      responseType: 'blob',
    });
  }

  /**
   * Download example PDF for a specific product
   */
  downloadProductExamplePDF(productId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/voucher-products/${productId}/example-pdf`, {
      responseType: 'blob',
    });
  }

  /**
   * Check if a voucher is a product voucher (has associated product)
   */
  isProductVoucher(voucher: Voucher | PublicVoucherVerification): boolean {
    return !!voucher.voucher_product_id;
  }

  /**
   * Check if a voucher is a money voucher (no associated product)
   */
  isMoneyVoucher(voucher: Voucher | PublicVoucherVerification): boolean {
    return !this.isProductVoucher(voucher);
  }

  /**
   * Get voucher type as string
   */
  getVoucherType(voucher: Voucher | PublicVoucherVerification): 'product' | 'money' {
    return this.isProductVoucher(voucher) ? 'product' : 'money';
  }

  /**
   * Get the full URL for a product image
   */
  getProductImageUrl(imagePath: string | null): string | null {
    if (!imagePath) return null;
    return `${environment.apiUrl}/images/${imagePath}`;
  }
}

export interface VoucherRedemption {
  id: number;
  amount: number;
  balance_before?: number | null;
  balance_after?: number | null;
  redeemed_at: string;
  redeeming_company_id?: string | null;
  redeeming_company_name?: string | null;
}

export interface VoucherProductData {
  public_id: string;
  name: string;
  description: string | null;
  price: number; // Price in cents (full face value / redeemable)
  discount_percentage?: number; // 0-50, customer pays reduced price
  image_path: string | null;
  image_cropped_path: string | null;
}

export interface Voucher {
  amount: number;
  initial_amount: number;
  personal_message: string;
  recipient_name: string;
  recipient_email: string;
  public_company_id: number;
  id: number;
  code: string;
  verification_key?: string; // For unified QR code (both public and in-app redemption)
  status: 'active' | 'redeemed' | 'expired' | 'refunded';
  created_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  refunded_at: string | null;
  redemptions: VoucherRedemption[];
  voucher_product_id?: string | null; // Product ID if this is a product voucher
  voucher_product_version_id?: string | null; // Product version ID at purchase time
  voucher_product_data?: VoucherProductData | null; // Product details from version (computed)
  is_internal?: boolean; // Raffle/giveaway voucher: no payment, not billed
  internal_note?: string | null; // Why it was issued, e.g. "Verlosung Sommerfest"
}

export interface InternalVoucherCreate {
  amount?: number; // in cents, for money vouchers
  voucher_product_id?: string; // for product vouchers
  recipient_name?: string;
  personal_message?: string;
  internal_note?: string;
  quantity?: number;
}

export interface InternalVoucherCreateResponse {
  vouchers: Voucher[];
  count: number;
}

export interface PaginatedVoucherResponse {
  items: Voucher[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PublicVoucherVerification {
  code: string;
  amount: number;
  initial_amount: number;
  status: 'active' | 'redeemed' | 'expired' | 'refunded';
  created_at: string;
  expires_at: string | null;
  company_name: string;
  public_company_id?: string | null;
  /** The voucher's company's currency (ISO 4217) and locale; amounts are in its cents */
  currency?: string;
  currency_locale?: string;
  redemptions: VoucherRedemption[];
  voucher_product_id?: string | null; // Product ID if this is a product voucher
  voucher_product_version_id?: string | null; // Product version ID at purchase time
  voucher_product_data?: VoucherProductData | null; // Product details from version (computed)
}

export interface PublicRedemptionResponse {
  message: string;
  redeemed_amount: number;
  remaining_amount: number;
  redeemed_at: string;
}
